import { UICell, UISheet } from "../../types/UITypes"
import { useContext, useMemo } from "react"
import "./SheetRenderer.css"
import { AppContext } from "../../AppContext"
import clsx from "clsx"
import { useReactFlow } from "reactflow"

type CellContextMenuProps = {
  cell: UICell
  cellWithContextMenu?: { cellId: string; target: "rowIndex" | "cell" }
  sheet: UISheet
  target: "rowIndex" | "cell"
  showColumnContextMenu?: boolean
  showRowContextMenu?: boolean
  onMoveRow?: (rowIndex: number) => void
}

export const CellContextMenu = ({
  cell,
  cellWithContextMenu,
  sheet,
  target,
  showColumnContextMenu = false,
  showRowContextMenu = false,
  onMoveRow,
}: CellContextMenuProps) => {
  const {
    onAddRow,
    onRemoveRow,
    isPendingApiResponse,
    onAddColumn,
    onRemoveColumn,
    multiselectedCells,
    setCellWithContextMenu,
    onPasteCells,
  } = useContext(AppContext)

  const nodes = useReactFlow().getNodes()

  const isMenuOpen = useMemo(
    () =>
      cellWithContextMenu?.cellId === cell.id &&
      cellWithContextMenu?.target === target,
    [cellWithContextMenu, cell.id, target]
  )

  if (!isMenuOpen) {
    return null
  }

  return (
    <div
      key={cell.id}
      className={clsx("cell-menu", {
        "cell-menu-disabled": isPendingApiResponse,
      })}
    >
      {showRowContextMenu && (
        <>
          <div
            className="cell-menu-item"
            onClick={() =>
              !isPendingApiResponse && onAddRow(sheet.id, cell.rowIndex)
            }
          >
            Add Row
          </div>
          <div
            className="cell-menu-item"
            onClick={() => onRemoveRow(sheet.id, cell.rowIndex)}
          >
            Delete Row
          </div>
          {onMoveRow && (
            <div
              className="cell-menu-item"
              onClick={() => {
                onMoveRow(cell.rowIndex)
              }}
            >
              Move Row
            </div>
          )}
        </>
      )}
      {showColumnContextMenu && (
        <>
          <div
            className="cell-menu-item"
            onClick={() =>
              !isPendingApiResponse && onAddColumn(sheet.id, cell.columnIndex)
            }
          >
            Add Column
          </div>
          <div
            className="cell-menu-item"
            onClick={() => onRemoveColumn(sheet.id, cell.columnIndex)}
          >
            Delete Column
          </div>
        </>
      )}
      <div
        className="cell-menu-item"
        onClick={() => {
          let copiedCells = [cell.name]
          if (
            multiselectedCells?.sheetId &&
            multiselectedCells?.sheetId === sheet.id
          ) {
            copiedCells = nodes
              .find((node) => node.data.id === multiselectedCells.sheetId)
              ?.data?.cells.filter((cell: any) => {
                return (
                  cell.rowIndex >= multiselectedCells?.startRow &&
                  cell.rowIndex <= multiselectedCells?.endRow &&
                  cell.columnIndex >= multiselectedCells?.startColumn &&
                  cell.columnIndex <= multiselectedCells?.endColumn
                )
              })
            const rows: any = []
            copiedCells.forEach((cell: any) => {
              if (!rows[cell.rowIndex - multiselectedCells.startRow]) {
                rows[cell.rowIndex - multiselectedCells.startRow] = []
              }
              rows[cell.rowIndex - multiselectedCells.startRow][
                cell.columnIndex - multiselectedCells.startColumn
              ] = cell.name
            })
            const copiedCellsString = rows
              .map((row: any) => {
                return row.join("\t")
              })
              .join("\n")

            console.log("copiedCellsString", "\n", copiedCellsString)
            navigator.clipboard.writeText(copiedCellsString)
            setCellWithContextMenu(undefined)
          }
        }}
      >
        Copy
      </div>
      <div
        className="cell-menu-item"
        onClick={async () => {
          const copiedCells = await navigator.clipboard.readText()
          const rows = copiedCells
            .replaceAll("\r", "")
            .split("\n")
            .map((row) => row.split("\t"))

          const pastedCells: Record<string, string> = {}
          rows.forEach((row, rowIndex) => {
            row.forEach((rowCell, columnIndex) => {
              pastedCells[
                `${cell.rowIndex + rowIndex}-${cell.columnIndex + columnIndex}`
              ] = rowCell
            })
          })

          console.log({ pastedCells })
          onPasteCells(sheet.id, pastedCells)
          setCellWithContextMenu(undefined)
        }}
      >
        Paste
      </div>
    </div>
  )
}
export default CellContextMenu
